import React, { useEffect, useState, useCallback } from "react";
import {
  Container,
  Grid,
  TextField,
  Modal,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  Box,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  Tooltip,
  Divider,
  Chip,
  Checkbox,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import { jsPDF } from "jspdf";
import { Navigate, useNavigate } from "react-router-dom";
import { Snackbar, Alert } from "@mui/material";
// ─────────────────────────────────────────────
// Info data for the dialog
// ─────────────────────────────────────────────
const leavingCertInfoSections = [
  {
    title: "Select Student",
    color: "#1976D2",
    description: "Search and select the student for whom you want to generate a School Leaving Certificate. Click the search icon to open the student selection dialog.",
    fields: [
      "Click the 🔍 icon to open the student list",
      "Search by Student Name, Admission No, Class, Section, Roll No, or Father's Name",
      "Filter by Class using the dropdown inside the dialog",
      "Click 'Select' on any row to pick that student",
    ],
  },
  {
    title: "Form Fields",
    color: "#7B1FA2",
    description: "Fill in the required details to generate the SLC. Some fields may be hidden depending on your school configuration.",
    fields: [
      "Selected Student – Auto-filled after selection (read-only)",
      "Leaving Date – Date the student is leaving (required)",
      "Reason for Leaving – Optional field (hidden for IDEAL clients)",
      "Last Class Attended – Select from available classes (required)",
      "Remarks – Any additional notes (optional)",
    ],
  },
  {
    title: "Generate SLC",
    color: "#388E3C",
    description: "Click 'Generate SLC' to submit the form and create the School Leaving Certificate record.",
    fields: [
      "Requires: Student, Leaving Date, Last Class Attended",
      "Reason for Leaving is also required for non-IDEAL clients",
      "On success – a confirmation modal appears with VIEW and CLOSE options",
      "On duplicate – an error dialog appears if SLC already exists for the student",
      "The student is removed from the list after a successful SLC generation",
    ],
  },
  {
    title: "View SLC PDF",
    color: "#F57C00",
    description: "After generating the SLC, click VIEW in the success modal to generate and open the SLC as a PDF in a new browser tab.",
    fields: [
      "Generates a formatted A4 PDF with school logo and details",
      "PDF includes: Student Name, Father's/Mother's Name, DOB (with words), Place of Birth, Address, Admission Date, Leaving Date, Last Class, Remarks",
      "Conditionally shows/hides: Religion, Caste, Reason for Leaving, Dues Amount (based on client config)",
      "Shows a loading spinner while PDF is being generated",
      "PDF opens in a new browser tab",
    ],
  },
  {
    title: "Actions",
    color: "#C62828",
    description: "Use the action buttons to manage the form.",
    fields: [
      "GENERATE SLC – Submits the form and creates the certificate",
      "RESET – Clears all form fields without navigating away",
      "BACK – Navigates back to the previous page",
    ],
  },
];

const LeavingCertificate = () => {
  const [unpaidRecords, setUnpaidRecords] = useState([]);
const [unpaidLoading, setUnpaidLoading] = useState(false);
const [selectedFeeIds, setSelectedFeeIds] = useState([]);
const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
const [isDeleting, setIsDeleting] = useState(false);
const [deleteProgress, setDeleteProgress] = useState({ done: 0, total: 0 });
  const [duesDialogOpen, setDuesDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
const [snackbarMessage, setSnackbarMessage] = useState("");
const [snackbarSeverity, setSnackbarSeverity] = useState("success"); // "success" | "error" | "warning"
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  // ── Info dialog state ──
  const [infoOpen, setInfoOpen] = useState(false);
  let navigate = useNavigate();
  const [studentId, setStudentId] = useState("");
  const [selectedStudentName, setSelectedStudentName] = useState("");
  const [leavingDate, setLeavingDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [reasonForLeaving, setReasonForLeaving] = useState("");
  const [lastClassAttended, setLastClassAttended] = useState("");
  const [remarks, setRemarks] = useState("");
  const [ddlClass, setDdlClass] = useState([]);
  const [ddlStudent, setDdlStudent] = useState([]);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [openStudentModal, setOpenStudentModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [selectedClass, setSelectedClass] = useState("");

  // ── Check if current client is IDEAL school ──
  const clientCode = (sessionStorage.getItem("clientCode") || "").trim().toUpperCase();
  const isIDEAL = clientCode === "IDEAL";

 const filteredStudents = ddlStudent.filter((student) => {
  const searchLower = searchQuery.toLowerCase();
  const classMatch = selectedClass ? student.className === selectedClass : true;
  return classMatch && (
    student.studentName.toLowerCase().includes(searchLower) ||
    student.admissionNo.toLowerCase().includes(searchLower) ||
    student.className.toLowerCase().includes(searchLower) ||
    student.sectionName.toLowerCase().includes(searchLower) ||
    student.rollNo.toLowerCase().includes(searchLower) ||
    student.fatherName.toLowerCase().includes(searchLower)
  );
});


  const paginatedStudents = filteredStudents.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

 const fetchStudent = async () => {
  try {
    const apiUrl = process.env.REACT_APP_BASE_URL;
    const token = sessionStorage.getItem("token");
    const response = await fetch(`${apiUrl}/SLC/GetStudentList_SLC`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
      body: JSON.stringify({
        classId: "0",
      }),
    });
    if (!response.ok) {
      throw new Error(`Error fetching students: ${response.status}`);
    }
    const data = await response.json();
    if (data.data === null && data.msg === "Record Not Found") {
      setDdlStudent([]);
      return;
    }
    // Ensure we always set an array
    setDdlStudent(Array.isArray(data) ? data : data.data || []);
  } catch (error) {
    console.error(error);
    setDdlStudent([]); // Always set empty array on error
  }
};

 const fetchClass = async () => {
  try {
    const apiUrl = process.env.REACT_APP_BASE_URL;
    const token = sessionStorage.getItem("token");
    const response = await fetch(`${apiUrl}/Enquiry/ddlClassName`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
      body: JSON.stringify({}),
    });
    if (!response.ok) {
      throw new Error(`Error fetching classes: ${response.status}`);
    }
    const data = await response.json();
    if (data.data === null && data.msg === "Record Not Found") {
      setDdlClass([]);
      return;
    }
    // Ensure we always set an array
    setDdlClass(Array.isArray(data) ? data : data.data || []);
  } catch (error) {
    console.error(error);
    setDdlClass([]); // Always set empty array on error
  }
};

  useEffect(() => {
    fetchClass();
    fetchStudent();
  }, []);

  const handleCancel = () => {
    setStudentId("");
    setSelectedStudentName("");
    setLeavingDate(dayjs().format("YYYY-MM-DD"));
    setReasonForLeaving("");
    setLastClassAttended("");
    setRemarks("");
  };

  // ── Fetch unpaid records (new API) ──
const fetchUnpaidRecords = async (sid) => {
  setUnpaidLoading(true);
  try {
    const url = process.env.REACT_APP_BASE_URL;
    const token = sessionStorage.getItem("token");

    const res = await fetch(
      `${url}/FeeDeposit/GetAllUnpaidRecord_Delete?StudentId=${sid}`,
      {
        method: "GET",
        headers: { "Content-Type": "application/json", Authorization: token },
      }
    );
    if (!res.ok) throw new Error(`Unpaid fetch failed: ${res.status}`);

    const data = await res.json();
    let records = [];
    if (data.status === "Succeed" && data.feeRecord) {
      records = Array.isArray(data.feeRecord) ? data.feeRecord : [data.feeRecord];
    }
    setUnpaidRecords(records);
    setSelectedFeeIds([]);
    return records;            // [] = no unpaid records
  } catch (error) {
    console.error(error);
    setUnpaidRecords([]);
    setSnackbarMessage("Could not check unpaid records. Please try again.");
    setSnackbarSeverity("error");
    setSnackbarOpen(true);
    return null;               // null = API failed
  } finally {
    setUnpaidLoading(false);
  }
}; 

// ── Selection helpers ──
const handleToggleRecord = (id) => {
  setSelectedFeeIds((prev) =>
    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
  );
};

const handleToggleAll = (checked) => {
  setSelectedFeeIds(checked ? unpaidRecords.map((r) => r.feePaymentID) : []);
};

// ── Bulk delete (runs the DELETE API in a loop) ──
const handleConfirmBulkDelete = async () => {
  setDeleteConfirmOpen(false);
  setIsDeleting(true);
  setDeleteProgress({ done: 0, total: selectedFeeIds.length });

  const url = process.env.REACT_APP_BASE_URL;
  const token = sessionStorage.getItem("token");
  let success = 0;
  let failed = 0;

  for (const feeGenId of selectedFeeIds) {
    try {
      const res = await fetch(`${url}/FeeDeposit/DeleteFee?FeeGenId=${feeGenId}`, {
        method: "DELETE",
        headers: { Authorization: token },
      });
      const data = await res.json();
      if (res.ok && data.status === "Succeed") success++;
      else failed++;
    } catch (err) {
      console.error("Delete failed for", feeGenId, err);
      failed++;
    }
    setDeleteProgress((p) => ({ ...p, done: p.done + 1 }));
  }

  // Refresh list from the server
  const remaining = await fetchUnpaidRecords(studentId);
  setIsDeleting(false);

  if (failed > 0) {
    setSnackbarMessage(`${success} deleted, ${failed} failed.`);
    setSnackbarSeverity("warning");
  } else {
    setSnackbarMessage(`${success} record(s) deleted successfully.`);
    setSnackbarSeverity("success");
  }
  setSnackbarOpen(true);

  // Nothing left → close dialog, user can retry Generate SLC
  if (remaining.length === 0) {
    setDuesDialogOpen(false);
    setSnackbarMessage("All unpaid records cleared. Click 'Generate SLC' again.");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  }
};

const handleSubmit = async () => {
  try {
    const url = process.env.REACT_APP_BASE_URL;
    const token = sessionStorage.getItem("token");

  // ── STEP 1: Check unpaid records (new API) ──
const unpaid = await fetchUnpaidRecords(studentId);

if (unpaid === null) return;   // API failed, snackbar already shown

// ── STEP 2: Block if unpaid records exist ──
if (unpaid.length > 0) {
  setDuesDialogOpen(true);
  return;
}

    // ── STEP 3: Dues clear — generate SLC ──
    const formattedDate = dayjs(leavingDate).format("YYYY-MM-DD");

    const payload = {
      studentId: parseInt(studentId),
      leavingDate: formattedDate,
      reasonForLeaving: reasonForLeaving,
      lastClassAttended: lastClassAttended,
      remarks: remarks,
    };

    const response = await fetch(`${url}/SLC/generate-slc`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (response.ok && data.message === "SLC generated successfully") {
      const updatedStudents = [...ddlStudent];
      const studentIndex = updatedStudents.findIndex(
        (s) => s.studentId === parseInt(studentId)
      );
      if (studentIndex !== -1) {
        updatedStudents.splice(studentIndex, 1);
        setDdlStudent(updatedStudents);
      }
      setOpenSuccessModal(true);

    } else if (data.message === "SLC already exists for this student.") {
      setOpenErrorModal(true);
    } else {
      throw new Error(`Error generating SLC: ${response.status}`);
    }

  } catch (error) {
    console.error("Error submitting form:", error);
    setSnackbarMessage("Something went wrong. Please try again.");
    setSnackbarSeverity("error");
    setSnackbarOpen(true);
  }
}; 

  const handleCloseModal = () => {
    setOpenSuccessModal(false);
    setOpenErrorModal(false);
    handleCancel();
  };

  const handleViewSLC = async () => {
    try {
      setIsPdfLoading(true);
      const token = sessionStorage.getItem("token");
      const url = process.env.REACT_APP_BASE_URL;
      const apiUrl = `${url}/SLC/get-slc/${studentId}`;

      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const data = await response.json();
      if (data && data.length > 0) {
        generatePDF(data);
      } else {
        console.error("No SLC data returned for the student");
      }
    } catch (error) {
      console.error("Error fetching the PDF:", error);
    }
  };

 const generatePDF = useCallback(async (data) => {

    // ── CHANGE 1: fetchSchoolDetails helper (fetches from API, falls back to sessionStorage) ──
    const fetchSchoolDetails = async () => {
      try {
        const token = sessionStorage.getItem("token");
        if (!token) throw new Error("No authentication token found");

        const response = await fetch(
          "https://wsmsv1.aidigitalsec.com/api/FeeReport/GetSchoolName",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: token,
            },
            body: JSON.stringify({}),
          }
        );

        if (!response.ok)
          throw new Error(`API error: ${response.status} ${response.statusText}`);
        const apiData = await response.json();
        const school = apiData[0] || {};

        return {
          schoolName: school.schoolName?.replace(/['"]+/g, "") || "N/A",
          address: school.address?.replace(/['"]+/g, "") || "",
          phoneNo: school.phoneNo?.replace(/['"]+/g, "") || "",
        };
      } catch (error) {
        console.error("Error fetching school details:", error);
        return {
          schoolName:
            sessionStorage.getItem("organizationName")?.replace(/['"]+/g, "") ||
            "N/A",
          address:
            sessionStorage.getItem("address")?.replace(/['"]+/g, "") || "",
          phoneNo:
            sessionStorage.getItem("phoneNo")?.replace(/['"]+/g, "") || "",
        };
      }
    };

    // ── CHANGE 2: formatDate helper ──
    const formatDate = (dateString) => {
      if (!dateString) return "N/A";
      // Handles "2/10/2026 12:00:00 AM" and ISO "2026-02-10T00:00:00"
      return dateString.split(/[\sT]/)[0];
    };

    // Helper function to convert date to words
    const convertDateToWords = (dateStr) => {
      if (!dateStr || typeof dateStr !== 'string') return "N/A";

      const dateParts = dateStr.split(/[/\s:]+/);
      let day, month, year;

      if (dateParts.length > 3) {
        [month, day, year] = dateParts;
      } else {
        [day, month, year] = dateParts;
      }

      day = parseInt(day, 10);
      month = parseInt(month, 10);
      year = parseInt(year, 10);

      if (isNaN(day) || isNaN(month) || isNaN(year)) return "Invalid Date";

      const ones = ["", "First", "Second", "Third", "Fourth", "Fifth", "Sixth", "Seventh", "Eighth", "Ninth"];
      const tens = ["", "Tenth", "Eleventh", "Twelfth", "Thirteenth", "Fourteenth", "Fifteenth", "Sixteenth", "Seventeenth", "Eighteenth", "Nineteenth"];
      const twenties = ["", "", "Twentieth", "Thirtieth"];
      let dayInWords = "";
      if (day <= 19) {
        dayInWords = day < 10 ? ones[day] : tens[day - 10];
      } else {
        const dayTens = Math.floor(day / 10);
        const dayOnes = day % 10;
        dayInWords = (dayTens <= 3 ? twenties[dayTens] : ones[dayTens] + "ieth").replace("Fifth", "Fifth").replace("Ninth", "Ninth");
        if (dayOnes > 0) dayInWords = dayInWords.replace(/ieth$/, "") + "-" + ones[dayOnes].toLowerCase();
      }

      const months = ["", "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
      const monthInWords = months[month] || "Unknown Month";

      const yearToWords = (year) => {
        const numWords = {
          0: "Zero", 1: "One", 2: "Two", 3: "Three", 4: "Four", 5: "Five",
          6: "Six", 7: "Seven", 8: "Eight", 9: "Ninth", 10: "Ten",
          11: "Eleven", 12: "Twelve", 13: "Thirteen", 14: "Fourteen", 15: "Fifteen",
          16: "Sixteen", 17: "Seventeen", 18: "Eighteen", 19: "Nineteen",
          20: "Twenty", 30: "Thirty", 40: "Forty", 50: "Fifty",
          60: "Sixty", 70: "Seventy", 80: "Eighty", 90: "Ninety"
        };
        if (year < 20) return numWords[year];
        if (year < 100) {
          const tensPart = Math.floor(year / 10) * 10;
          const onesPart = year % 10;
          return onesPart ? `${numWords[tensPart]}-${numWords[onesPart].toLowerCase()}` : numWords[tensPart];
        }
        if (year < 1000) {
          const hundreds = Math.floor(year / 100);
          const remainder = year % 100;
          return remainder ? `${numWords[hundreds]} Hundred ${yearToWords(remainder)}` : `${numWords[hundreds]} Hundred`;
        }
        if (year < 10000) {
          const thousands = Math.floor(year / 1000);
          const remainder = year % 1000;
          return remainder ? `${numWords[thousands]} Thousand ${yearToWords(remainder)}` : `${numWords[thousands]} Thousand`;
        }
        return "Year Out of Range";
      };
      const yearInWords = yearToWords(year);

      return `${dayInWords} ${monthInWords} ${yearInWords}`;
    };

    try {
      const doc = new jsPDF("p", "mm", "a4");

      // Add custom font (fallback to Helvetica if font loading fails)
      doc.setFont("helvetica", "normal");

      // Add borders with rounded corners
      doc.setDrawColor(0, 51, 102); // Primary blue
      doc.setLineWidth(1.2);
      doc.roundedRect(8, 8, 194, 281, 3, 3); // Outer rounded border
      doc.setDrawColor(0, 102, 204); // Lighter blue
      doc.setLineWidth(0.4);
      doc.roundedRect(12, 12, 186, 273, 2, 2); // Inner rounded border

      // Add logo to PDF (if available)
      let logoDataUrl = null;
      let imageFormat = 'PNG';
      try {
        const token = sessionStorage.getItem("token");
        if (!token) throw new Error("No authentication token found");
        const response = await fetch('https://wsmsv1.aidigitalsec.com/api/FeeReport/GetSchoolName', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token,
          },
          body: JSON.stringify({}),
        });
        if (!response.ok) throw new Error(`API error: ${response.status} ${response.statusText}`);
        const apiData = await response.json();
        const logoImg = apiData[0]?.logoImg;

        if (logoImg && typeof logoImg === 'string' && logoImg.trim() !== '') {
          let cleanedBase64 = logoImg
            .replace(/^data:image\/[a-zA-Z]+;base64,/, '')
            .replace(/\s/g, '')
            .replace(/\n/g, '');
          const paddingLength = (4 - (cleanedBase64.length % 4)) % 4;
          cleanedBase64 += '='.repeat(paddingLength);

          const isValidBase64 = /^[A-Za-z0-9+/=]+$/.test(cleanedBase64);
          if (!isValidBase64) {
            console.error('Invalid base64 string for logoImg:', cleanedBase64.substring(0, 100) + '...');
            throw new Error('Invalid base64 string for logoImg');
          }

          imageFormat = 'PNG';
          logoDataUrl = `data:image/png;base64,${cleanedBase64}`;

          try {
            doc.setFillColor(255, 255, 255);
            doc.rect(14, 14, 35, 35, "F");
            doc.addImage(logoDataUrl, 'PNG', 15, 15, 33, 33);
          } catch (error) {
            console.error('Error adding logo to PDF (PNG):', error.message);
            const transparentPlaceholder = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAAXNSR0IArs4c6QAAAA1JREFUGFdjYGBgYAAAAAUAAY+SkSAAAAAASUVORK5CYII=';
            doc.setFillColor(255, 255, 255);
            doc.rect(14, 14, 27, 27, "F");
            doc.addImage(transparentPlaceholder, 'PNG', 15, 15, 25, 25);
          }
        } else {
          console.warn('No valid logo image found. Using transparent placeholder.');
          const transparentPlaceholder = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAAXNSR0IArs4c6QAAAA1JREFUGFdjYGBgYAAAAAUAAY+SkSAAAAAASUVORK5CYII=';
          doc.setFillColor(255, 255, 255);
          doc.rect(14, 14, 27, 27, "F");
          doc.addImage(transparentPlaceholder, 'PNG', 15, 15, 25, 25);
        }
      } catch (error) {
        console.error('Error fetching or processing logo:', error);
        const transparentPlaceholder = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAAXNSR0IArs4c6QAAAA1JREFUGFdjYGBgYAAAAAUAAY+SkSAAAAAASUVORK5CYII=';
        doc.setFillColor(255, 255, 255);
        doc.rect(14, 14, 27, 27, "F");
        doc.addImage(transparentPlaceholder, 'PNG', 15, 15, 25, 25);
      }

      // ── CHANGE 3: School info now comes from fetchSchoolDetails API (not sessionStorage) ──
      const schoolDetails = await fetchSchoolDetails();
      const schoolName = schoolDetails.schoolName;
      const schoolAddress = schoolDetails.address;
      const contactInfo = schoolDetails.phoneNo;
      const contactInfoWithLabel = `Phone No: ${contactInfo}`;

      // School Name
      doc.setFont("helvetica", "bold");
      doc.setFontSize(22);
      doc.setTextColor(0, 51, 102);
      const maxSchoolNameWidth = 140;
      const splitSchoolName = doc.splitTextToSize(schoolName, maxSchoolNameWidth);

      // Calculate position for (Recognized)
      const schoolNameWidth = doc.getTextWidth(splitSchoolName[0]);
      const registeredX = 58 + schoolNameWidth + 2;

      // Draw school name
      doc.text(splitSchoolName, 55, 22);

      // ── CHANGE 4: "(Registered)" → "(Recognized)" ──
      doc.setFontSize(12);
      doc.setTextColor(100, 100, 100);
      doc.text("(Recognized)", registeredX, 22);

      // Reset styles
      doc.setFontSize(22);
      doc.setTextColor(0, 51, 102);

      const schoolNameHeight = splitSchoolName.length * 8;

      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(50, 50, 50);
      const addressStartX = 55;
      const maxAddressWidth = 140;
      const splitAddressText = doc.splitTextToSize(schoolAddress, maxAddressWidth);
      const addressLine1 = splitAddressText[0] || "";
      const addressLine2 = splitAddressText[1] || "";
      const addressY1 = 20 + schoolNameHeight + 3;
      const addressY2 = addressY1 + 5;
      doc.text(addressLine1, addressStartX, addressY1);
      doc.text(addressLine2, addressStartX, addressY2);

      // Contact Number
      doc.setFontSize(11);
      const contactY = addressY2 + 6;
      doc.text(contactInfoWithLabel, 55, contactY, { align: "left" });

      // Decorative divider
      doc.setDrawColor(0, 51, 102);
      doc.setLineWidth(0.3);
      const dividerY = contactY + 10;
      doc.setFillColor(0, 51, 102, 0.2);
      doc.rect(20, dividerY - 1, 170, 1, "F");

      // Title
      doc.setFontSize(30);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 51, 102);
      const titleY = dividerY + 15;
      doc.text("School Leaving Certificate", 105, titleY, { align: "center" });

      // Sub-title
      doc.setFontSize(13);
      doc.setFont("helvetica", "italic");
      doc.setTextColor(50, 50, 50);
      const subtitleY = titleY + 8;
      doc.text("This certifies that the following student has left the institution:", 105, subtitleY, { align: "center" });

      // TC Number
      const tcNo = data && data.length > 0 ? `TC.NO: ${data[0].slcId}` : "TC.NO: N/A";
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 51, 102);
      const tcNoY = subtitleY + 6;
      doc.text(tcNo, 18, tcNoY);

      // TC Number underline
      doc.setDrawColor(0, 51, 102);
      doc.setLineWidth(0.2);
      const tcNoLineY = tcNoY + 2;
      doc.line(15, tcNoLineY, 41, tcNoLineY);

      // Content
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);
      let contentY = tcNoLineY + 12;

      if (data && data.length > 0) {
        const slc = data[0];
        const dobInWords = convertDateToWords(slc.studentDob);

        // ── CHANGE 5: Conditionally hide Dues Amount for IDEAL client ──
        const clientCode = (sessionStorage.getItem("clientCode") || "").trim().toUpperCase();
        const isIDEAL = clientCode === "IDEAL";

        const details = [
          { label: "Student Name", value: slc.studentName },
          { label: "Admission No", value: slc.admissionNo },
          { label: "Aadhar No.", value: slc.adhaarNo },
          { label: "Father's Name", value: slc.fathersName },
          { label: "Mother's Name", value: slc.mothersName },
          { label: "Student DOB", value: slc.studentDob },
          { label: "in words", value: dobInWords },
          { label: "Place of Birth", value: "" },
          { label: "Address", value: slc.address },
          // ── CHANGE 6: Hide Religion & Caste row for IDEAL client in PDF ──
          ...(!isIDEAL ? [{ label: "Religion", value: "" }] : []),
          { label: "Admission Date", value: slc.admissionDate },
          { label: "Leaving Date", value: formatDate(slc.leavingDate) },
          // ── CHANGE 7: Hide Reason for Leaving for IDEAL client in PDF ──
          ...(!isIDEAL ? [{ label: "Reason for Leaving", value: slc.reasonForLeaving }] : []),
          { label: "Last Class Attended", value: slc.lastClassAttended },
          { label: "Remarks", value: slc.remarks },
          // ── CHANGE 5 (original): Hide Dues Amount for IDEAL client ──
          ...(!isIDEAL ? [{ label: "Dues Amount", value: slc.duesAmount }] : []),
          { label: "Issued Date", value: formatDate(slc.issuedDate) },
        ];

        const maxWidth = 120;
        const pageHeight = 277;
        const bottomMargin = 25;

        details.forEach(({ label, value }) => {
          const valueStr = value !== undefined && value !== null ? String(value) : "N/A";

          if (contentY + 12 > pageHeight - bottomMargin) {
            doc.addPage();
            contentY = 15;
            doc.setDrawColor(0, 51, 102);
            doc.setLineWidth(1.2);
            doc.roundedRect(8, 8, 194, 281, 3, 3);
            doc.setDrawColor(0, 102, 204);
            doc.setLineWidth(0.4);
            doc.roundedRect(12, 12, 186, 273, 2, 2);
          }

          doc.setFont("helvetica", "bold");
          doc.text(`${label}:`, 15, contentY);

          if (label === "Religion") {
            // Religion & Caste rendered inline (only shown for non-IDEAL clients)
            let currentX = 65;
            const y = contentY;
            doc.setFont("helvetica", "normal");
            const religionName = slc.religionName && slc.religionName.trim() !== "" ? slc.religionName : "N/A";
            doc.text(religionName, currentX, y);
            currentX += doc.getTextWidth(religionName) + 6;
            doc.setFont("helvetica", "bold");
            doc.text("Caste:", currentX, y);
            const casteLabelWidth = doc.getTextWidth("Caste:");
            doc.setFont("helvetica", "normal");
            const casteName = slc.casteName && slc.casteName.trim() !== "" ? slc.casteName : "N/A";
            doc.text(casteName, currentX + casteLabelWidth, y);
          } else if (label === "Place of Birth") {
            let currentX = 65;
            const y = contentY;
            doc.setFont("helvetica", "normal");
            const cityName = slc.cityName && slc.cityName.trim() !== "" ? slc.cityName : "N/A";
            doc.text(cityName, currentX, y);
            currentX += doc.getTextWidth(cityName) + 4;
            doc.setFont("helvetica", "bold");
            doc.text("State:", currentX, y);
            const stateLabelWidth = doc.getTextWidth("State:");
            doc.setFont("helvetica", "normal");
            const stateName = slc.stateName && slc.stateName.trim() !== "" ? slc.stateName : "N/A";
            doc.text(stateName, currentX + stateLabelWidth, y);
            currentX += stateLabelWidth + doc.getTextWidth(stateName) + 6;
            doc.setFont("helvetica", "bold");
            doc.text("Country:", currentX, y);
            const countryLabelWidth = doc.getTextWidth("Country:");
            doc.setFont("helvetica", "normal");
            const countryName = slc.countyName && slc.countyName.trim() !== "" ? slc.countyName : "N/A";
            doc.text(countryName, currentX + countryLabelWidth, y);
            currentX += countryLabelWidth + doc.getTextWidth(countryName) + 4;
            doc.setFont("helvetica", "bold");
            doc.text("Nationality:", currentX, y);
            const nationalityLabelWidth = doc.getTextWidth("Nationality:");
            doc.setFont("helvetica", "normal");
            const nationality = slc.nationality && slc.nationality.trim() !== "" ? slc.nationality : "N/A";
            doc.text(nationality, currentX + nationalityLabelWidth, y);
          } else {
            doc.setFont("helvetica", "normal");
            const splitText = doc.splitTextToSize(valueStr, maxWidth);
            doc.text(splitText, 65, contentY);
            contentY += (splitText.length - 1) * 6;
          }

          contentY += 8;
        });

        // Footer
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(60, 60, 60);
        doc.text('Certified that, above information is in accordance with the School Register.', 105, 236, { align: "center" });
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        const penaltyText = '(No change in any entry shall be made except by the authority issuing it and';
        const penaltyText2 = 'any infringement of the requirement is liable to involve the imposition of penalty such as that of rustication)';
        const splitPenaltyText = doc.splitTextToSize(penaltyText, 170);
        const splitPenaltyText2 = doc.splitTextToSize(penaltyText2, 170);
        doc.text(splitPenaltyText, 105, 244, { align: "center" });
        doc.text(splitPenaltyText2, 105, 249, { align: "center" });

        doc.setFontSize(11);
        doc.setFont("helvetica", "italic");
        doc.setTextColor(0, 51, 102);
        doc.text("Principal Signature", 145, 270);
        doc.setDrawColor(0, 51, 102);
        doc.setLineWidth(0.2);
        doc.line(135, 265, 185, 265);
      } else {
        console.error("Data array is empty or not defined");
      }

      // Output PDF
      const pdfBlob = doc.output("blob");
      const pdfUrl = URL.createObjectURL(pdfBlob);
      setIsPdfLoading(false);
      window.open(pdfUrl, "_blank");
    } catch (error) {
      setIsPdfLoading(false);
      alert("Error Generating PDF")
      console.error("Error generating the PDF:", error);
    }
  }, []);

  const handleOpenStudentModal = () => setOpenStudentModal(true);

  const handleSelectStudent = (student) => {
    setStudentId(student.studentId);
    setSelectedStudentName(student.studentName);
    setOpenStudentModal(false);
  };

  return (
    <Container maxWidth="md" style={{ marginTop: "30px" }}>
      <Box
        sx={{
          backgroundColor: '#0B1F40',
          padding: '12px 24px',
          boxShadow: 3,
          borderRadius: '4px 4px 0 0',
          margin: '2px auto',
          maxWidth: "900px",
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h4" sx={{ color: '#fff', textAlign: 'center', fontWeight: 'bold', flexGrow: 1 }}>
          School Leaving Form
        </Typography>

        {/* ── Info Button ── */}
        <IconButton
          onClick={() => setInfoOpen(true)}
          title="Form Information"
          style={{
            backgroundColor: "rgba(255,255,255,0.15)",
            border: "2px solid rgba(255,255,255,0.6)",
            borderRadius: "50%",
            padding: "6px",
            color: "#fff",
          }}
        >
          <InfoIcon style={{ fontSize: 26 }} />
        </IconButton>
      </Box>

      {/* ── Info Dialog ── */}
      <Dialog
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ style: { borderRadius: 16, padding: 0 } }}
      >
        <DialogTitle
          style={{
            backgroundColor: "#0B1F40",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 24px",
          }}
        >
          <Box display="flex" alignItems="center">
            <InfoIcon style={{ marginRight: 8 }} />
            <Typography variant="h6" style={{ fontWeight: "bold" }}>
              School Leaving Form — Form Guide
            </Typography>
          </Box>
        
        </DialogTitle>

        <DialogContent dividers style={{ backgroundColor: "#f9f9f9", padding: "24px" }}>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 20 }}>
            This form is used to generate a School Leaving Certificate (SLC) for a student. Fill in all required fields and click 'Generate SLC' to create the certificate.
          </Typography>

          {leavingCertInfoSections.map((section, index) => (
            <Box
              key={index}
              style={{
                backgroundColor: "#fff",
                borderRadius: 12,
                padding: "18px 20px",
                marginBottom: 16,
                borderLeft: `5px solid ${section.color}`,
                boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
              }}
            >
              <Typography
                variant="h6"
                style={{ fontWeight: "bold", color: section.color, marginBottom: 6 }}
              >
                {section.title}
              </Typography>

              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 10 }}>
                {section.description}
              </Typography>

              <Divider style={{ marginBottom: 10 }} />

              <Typography variant="caption" style={{ fontWeight: "bold", color: "#555", display: "block", marginBottom: 6 }}>
                📝 DETAILS
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {section.fields.map((field, i) => (
                  <Chip
                    key={i}
                    label={field}
                    size="small"
                    variant="outlined"
                    style={{
                      borderColor: section.color,
                      color: section.color,
                    }}
                  />
                ))}
              </Box>
            </Box>
          ))}
        </DialogContent>

        <DialogActions style={{ padding: "12px 24px", backgroundColor: "#f9f9f9" }}>
          <Button
            onClick={() => setInfoOpen(false)}
            variant="contained"
            style={{ backgroundColor: "#0B1F40", color: "#fff", borderRadius: 20, paddingInline: 28 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Paper elevation={3} style={{ padding: "20px", marginTop: 6 }}>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Selected Student"
              value={selectedStudentName}
              fullWidth
              variant="outlined"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleOpenStudentModal}>
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Leaving Date"
              type="date"
              value={leavingDate}
              onChange={(e) => setLeavingDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
              variant="outlined"
            />
          </Grid>

          {/* ── CHANGE 8: Hide Reason for Leaving field in the form for IDEAL client ── */}
          {!isIDEAL && (
            <Grid item xs={12} sm={6}>
              <TextField
                label="Reason for Leaving"
                value={reasonForLeaving}
                onChange={(e) => setReasonForLeaving(e.target.value)}
                fullWidth
                variant="outlined"
              />
            </Grid>
          )}

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Last Class Attended</InputLabel>
              <Select
                value={lastClassAttended}
                onChange={(e) => setLastClassAttended(e.target.value)}
                label="Last Class Attended"
              >
                <MenuItem value="">
                  <em>Select</em>
                </MenuItem>
                {ddlClass.map((item) => (
                  <MenuItem key={item.classId} value={item.className}>
                    {item.className}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Remarks"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              fullWidth
              variant="outlined"
              multiline
              rows={4}
            />
          </Grid>
          <Grid item xs={12} style={{ textAlign: "center" }}>
            {/* ── CHANGE 9: Reason for Leaving is excluded from required fields check for IDEAL client ── */}
            <Tooltip title={(!studentId || !leavingDate || (!isIDEAL && !reasonForLeaving) || !lastClassAttended) && "Please fill the required fields"} arrow>
            <span><Button
              variant="contained"
              sx={{
                marginRight: "16px",
              }}
              color="success"
              onClick={handleSubmit}
              disabled={!studentId || !leavingDate || (!isIDEAL && !reasonForLeaving) || !lastClassAttended}
            >
              Generate SLC
            </Button></span></Tooltip>
            <Button
              variant="contained"
              sx={{
                mr: 2,
                backgroundColor: "#2196f3",
                "&:hover": { backgroundColor: "#1976d2" },
              }}
              color="secondary"
              onClick={handleCancel}
            >
              RESET
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => { navigate(-1) }}
            >
              BACK
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Dialog
        open={openStudentModal}
        onClose={() => setOpenStudentModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Select Student</DialogTitle>
       <DialogContent>
  <Box sx={{ display: 'flex', gap: 2, marginBottom: "20px", marginTop: '20px' }}>
    <TextField
      label="Search Students"
      variant="outlined"
      fullWidth
      value={searchQuery}
      onChange={(e) => setSearchQuery(e.target.value)}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        ),
      }}
      sx={{ flex: 1 }}
    />
    <FormControl sx={{ minWidth: 150 }}>
      <InputLabel>Class</InputLabel>
      <Select
        value={selectedClass}
        onChange={(e) => setSelectedClass(e.target.value)}
        label="Class"
      >
        <MenuItem value="">All Classes</MenuItem>
        {ddlClass.map((cls) => (
          <MenuItem key={cls.classId} value={cls.className}>
            {cls.className}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Admission No</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Class</TableCell>
                  <TableCell>Section</TableCell>
                  <TableCell>Roll No</TableCell>
                  <TableCell>Father's Name</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedStudents.map((student) => (
                  <TableRow key={student.studentId}>
                    <TableCell>{student.admissionNo}</TableCell>
                    <TableCell>{student.studentName}</TableCell>
                    <TableCell>{student.className}</TableCell>
                    <TableCell>{student.sectionName}</TableCell>
                    <TableCell>{student.rollNo}</TableCell>
                    <TableCell>{student.fatherName}</TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => handleSelectStudent(student)}
                      >
                        Select
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {paginatedStudents.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No students found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredStudents.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setSearchQuery("");
              setPage(0);
              setOpenStudentModal(false);
            }}
            color="secondary"
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

     <Modal
  open={openSuccessModal}
  onClose={handleCloseModal}
  aria-labelledby="success-modal-title"
  aria-describedby="success-modal-description"
>
  <Box
    sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: 400,
      bgcolor: 'background.paper',
      borderRadius: 2,
      boxShadow: 24,
      overflow: 'hidden',
    }}
  >
    {/* Header */}
    <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
      <Typography id="success-modal-title" variant="h6" sx={{ color: '#fff' }}>
       SLC Generated ✅
      </Typography>
    </Box>

    {/* Content */}
    <Box sx={{ padding: '20px' }}>
      <Typography id="success-modal-description" sx={{ mb: 2 }}>
        The School Leaving Certificate has been generated successfully.
      </Typography>

      {/* Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
         <Button
          onClick={handleViewSLC}
          variant="contained"
          color="primary"
        >
          VIEW
        </Button>
      <Button
  onClick={() => {
    setOpenSuccessModal(false);
    handleCancel();
    setSearchQuery("");
    setSelectedClass("");
    setPage(0);
  }}
  variant="contained"
  color="error"
>
  CLOSE
</Button>
      </Box>
    </Box>
  </Box>
</Modal>

      <Dialog open={openErrorModal} onClose={handleCloseModal}>
        <DialogTitle>{"SLC Already Exists"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            An SLC already exists for this student.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseModal}
            color="secondary"
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
  open={isPdfLoading}
  PaperProps={{
    style: { background: "transparent", boxShadow: "none", overflow: "hidden" },
  }}
  sx={{ "& .MuiDialog-container": { alignItems: "center", justifyContent: "center" } }}
>
  <DialogContent
    sx={{ background: "white", padding: "20px", borderRadius: "8px", textAlign: "center", minWidth: "200px" }}
  >
    <div style={{
      width: "40px", height: "40px", border: "4px solid #f3f3f3",
      borderTop: "4px solid #3498db", borderRadius: "50%",
      animation: "spin 1s linear infinite", margin: "0 auto 15px",
    }} />
    <Typography>Generating PDF, please wait...</Typography>
  </DialogContent>
</Dialog>

<Snackbar
  open={snackbarOpen}
  autoHideDuration={5000}
  onClose={() => setSnackbarOpen(false)}
  anchorOrigin={{ vertical: "top", horizontal: "center" }}
>
  <Alert
    onClose={() => setSnackbarOpen(false)}
    severity={snackbarSeverity}
    variant="filled"
    sx={{ width: "100%" }}
  >
    {snackbarMessage}
  </Alert>
</Snackbar>


<Dialog
  open={duesDialogOpen}
  onClose={() => !isDeleting && setDuesDialogOpen(false)}
  maxWidth="md"
  fullWidth
>
  <DialogTitle sx={{ backgroundColor: "#C62828", color: "#fff", pb: 1 }}>
    ⚠️ Pending Dues — SLC Cannot Be Generated
  </DialogTitle>

  <DialogContent sx={{ pt: 2 }}>
   <Box
  sx={{
    backgroundColor: "#fff3f3",
    border: "1px solid #f44336",
    borderRadius: 2,
    padding: "12px 16px",
    mb: 2,
    mt: 2,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  }}
>
  <Typography variant="body1" color="error" fontWeight="bold">
    This student has unpaid fee records. Delete them to continue.
  </Typography>
  <Chip label={`${unpaidRecords.length} unpaid record(s)`} color="error" />
</Box>

    {unpaidLoading ? (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <CircularProgress size={32} />
      </Box>
    ) : (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead sx={{ backgroundColor: "#0B1F40" }}>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  sx={{ color: "#fff", "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: "#fff" } }}
                  indeterminate={
                    selectedFeeIds.length > 0 &&
                    selectedFeeIds.length < unpaidRecords.length
                  }
                  checked={
                    unpaidRecords.length > 0 &&
                    selectedFeeIds.length === unpaidRecords.length
                  }
                  onChange={(e) => handleToggleAll(e.target.checked)}
                  disabled={unpaidRecords.length === 0}
                />
              </TableCell>
              {["Receipt No", "Month", "Session"].map((h) => (
                <TableCell key={h} sx={{ color: "#fff", fontWeight: "bold" }}>{h}</TableCell>
              ))}
              {["Total Fee", "Fine", "Discount", "Paid"].map((h) => (
                <TableCell key={h} sx={{ color: "#fff", fontWeight: "bold" }} align="right">{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {unpaidRecords.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">No unpaid records found</TableCell>
              </TableRow>
            )}
            {unpaidRecords.map((row) => (
              <TableRow
                key={row.feePaymentID}
                hover
                selected={selectedFeeIds.includes(row.feePaymentID)}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedFeeIds.includes(row.feePaymentID)}
                    onChange={() => handleToggleRecord(row.feePaymentID)}
                  />
                </TableCell>
                <TableCell>{row.receiptNo}</TableCell>
                <TableCell>{row.monthName}</TableCell>
                <TableCell>{row.session}</TableCell>
                <TableCell align="right">₹{Number(row.totalFee || 0).toLocaleString("en-IN")}</TableCell>
                <TableCell align="right">₹{Number(row.fine || 0).toLocaleString("en-IN")}</TableCell>
                <TableCell align="right">₹{Number(row.discountAmount || 0).toLocaleString("en-IN")}</TableCell>
                <TableCell align="right">₹{Number(row.paidAmount || 0).toLocaleString("en-IN")}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )}

    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: "block" }}>
      * Clear or delete pending records before generating the SLC.
    </Typography>
  </DialogContent>

  <DialogActions sx={{ padding: "12px 24px", justifyContent: "space-between" }}>
    <Button
      variant="contained"
      color="error"
      startIcon={<DeleteIcon />}
      disabled={selectedFeeIds.length === 0}
      onClick={() => setDeleteConfirmOpen(true)}
    >
      Delete Selected ({selectedFeeIds.length})
    </Button>
    <Button
      onClick={() => setDuesDialogOpen(false)}
      variant="contained"
      sx={{ backgroundColor: "#0B1F40", color: "#fff" }}
    >
      Close
    </Button>
  </DialogActions>
</Dialog>

{/* ── Delete confirmation ── */}
<Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
  <DialogTitle>Confirm Delete</DialogTitle>
  <DialogContent>
    <DialogContentText>
      Are you sure you want to delete {selectedFeeIds.length} selected record(s)?
      This action cannot be undone.
    </DialogContentText>
  </DialogContent>
  <DialogActions>
    <Button onClick={() => setDeleteConfirmOpen(false)} variant="outlined">
      Cancel
    </Button>
    <Button onClick={handleConfirmBulkDelete} variant="contained" color="error">
      Delete
    </Button>
  </DialogActions>
</Dialog>

{/* ── Full-screen blurred loader (blocks all clicks) ── */}
<Backdrop
  open={isDeleting}
  sx={{
    zIndex: (theme) => theme.zIndex.modal + 10,  // above every dialog
    color: "#fff",
    backdropFilter: "blur(6px)",
    backgroundColor: "rgba(0,0,0,0.35)",
    display: "flex",
    flexDirection: "column",
    gap: 2,
  }}
>
  <CircularProgress color="inherit" size={60} />
  <Typography variant="h6">
    Deleting records... {deleteProgress.done}/{deleteProgress.total}
  </Typography>
</Backdrop>

    </Container>
  );
};

export default LeavingCertificate;